import { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Stack } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import authFetch from '../utils/authFetch';

// Internal profit summary for a single order (visible to staff only).
// Shows nothing until items carry cost snapshots (i.e. the order was delivered).
const ProfitSummary = ({ orderId, orderStatus }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch(`/order_items?filter=${encodeURIComponent(JSON.stringify({ order_id: orderId }))}&_start=0&_end=100`)
      .then(r => r.json())
      .then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [orderId, orderStatus]);

  const itemsWithCost = items.filter(i => i.unit_cost_ils_snapshot != null);
  if (loading || itemsWithCost.length === 0) return null;

  const fmtIls = v => `₪${Number(v||0).toLocaleString('he-IL',{minimumFractionDigits:0})}`;
  const totalRevenue = items.reduce((s,i)=>s+Number(i.price_at_purchase||0)*Number(i.quantity||1),0);
  const totalCost    = itemsWithCost.reduce((s,i)=>s+Number(i.unit_cost_ils_snapshot||0)*Number(i.quantity||1),0);
  const totalProfit  = itemsWithCost.reduce((s,i)=>s+Number(i.profit_ils||0),0);
  const margin       = totalRevenue > 0 ? (totalProfit/totalRevenue*100).toFixed(1) : null;
  const isPositive   = totalProfit >= 0;

  return (
    <Card sx={{ borderRadius: 2, mb: 3, border: `1.5px solid ${isPositive ? '#c3e6cb' : '#f5c6cb'}`,
      backgroundColor: isPositive ? 'rgba(212,237,218,0.15)' : 'rgba(248,214,218,0.15)' }}>
      <CardContent className="crm-card-content" sx={{ p: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
          <TrendingUpIcon sx={{ color: isPositive ? '#2e7d32' : '#c62828', fontSize: 20 }} />
          <Typography fontWeight={700} color="#0d1d3e" fontSize={14}>דוח רווח — הזמנה זו</Typography>
          <Typography variant="caption" color="text.secondary">(פנימי — לא מוצג ללקוח)</Typography>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4,1fr)' }, gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">הכנסה ממכירה</Typography>
            <Typography fontWeight={700} color="#0d1d3e">{fmtIls(totalRevenue)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">עלות סחורה</Typography>
            <Typography fontWeight={700} color="#856404">{fmtIls(totalCost)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">רווח גולמי</Typography>
            <Typography fontWeight={800} fontSize={16} color={isPositive ? '#2e7d32' : '#c62828'}>
              {fmtIls(totalProfit)}
            </Typography>
          </Box>
          {margin && (
            <Box>
              <Typography variant="caption" color="text.secondary">מרווח</Typography>
              <Typography fontWeight={700} fontSize={15} color={isPositive ? '#2e7d32' : '#c62828'}>
                {margin}%
              </Typography>
            </Box>
          )}
        </Box>
        {items.length > itemsWithCost.length && (
          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            * חלק מהפריטים אין להם נתוני עלות (הזמנה טרם אושרה/נשלחה)
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfitSummary;
